
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const aiActivities = [
  {
    id: 1,
    title: 'AI 비서와 함께하는 디자인 교실',
    date: '',
    description: '제미나이, 챗GPT, 이미지FX, 미리캔버스를 활용하여 웹포스터, 썸네일, PPT, 스마트스토어 상세페이지, 리플릿을 직접 제작하는 8주 실전 과정입니다.',
    thumbnail: 'https://static.readdy.ai/image/3c653b3a8a919d24e4f7a2804547f79c/e8ba3bf44f4d650e53a46ab06522d21a.png',
    target: '30~60대',
    link: '/ai-design-class',
    badge: 'AI 교육',
    badgeColor: '#00BFA5',
  },
  {
    id: 2,
    title: 'AI 유튜브 쇼츠 클래스',
    date: '채널 개설 + 쇼츠 4개 제작',
    description: '챗GPT, 제미나이, Suno, ImageFX, Grok, 캡컷, Sora2 등 다양한 생성형 AI 도구로 유튜브 쇼츠 영상을 기획부터 제작까지 완성하는 1인 크리에이터 양성 과정입니다.',
    thumbnail: 'https://static.readdy.ai/image/3c653b3a8a919d24e4f7a2804547f79c/f2823cfdff48b337d23ca4d36627c3d3.png',
    target: '성인',
    link: '/ai-youtube-shorts',
    badge: 'AI 교육',
    badgeColor: '#00BFA5',
  },
  {
    id: 3,
    title: 'Readdy AI로 홈페이지 만들기',
    date: '홈페이지 1개 완성·배포',
    description: '코딩 지식 없이 Readdy AI를 활용하여 홈페이지를 기획·디자인·제작·배포하는 전 과정을 배우고, 6주 만에 나만의 홈페이지를 완성하는 실전 과정입니다.',
    thumbnail: 'https://static.readdy.ai/image/3c653b3a8a919d24e4f7a2804547f79c/b12cd3b374ae88679ff4f3574635d827.png',
    target: '누구나',
    link: '/readdy-homepage',
    badge: 'AI 교육',
    badgeColor: '#00BFA5',
  },
];

export default function AISection() {
  const navigate = useNavigate();

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @font-face {
        font-family: 'FounderYooGeonWook';
        src: url('https://cdn.jsdelivr.net/gh/projectnoonnu/noonfonts_2112@1.0/establishRetrosansOTF.woff') format('woff');
        font-weight: normal;
        font-display: swap;
      }
    `;
    document.head.appendChild(style);
  }, []);

  const handleCardClick = (link: string) => {
    if (link) {
      navigate(link);
    }
  };

  return (
    <section id="ai" className="py-16 md:py-24 px-4 md:px-6" style={{ background: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)' }}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-16">
          <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl mx-auto mb-4 md:mb-6" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
            <i className="ri-robot-2-line text-3xl md:text-4xl text-white"></i>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold mb-3 md:mb-4" style={{ color: '#00BFFF', fontFamily: '\'FounderYooGeonWook\', sans-serif' }}>
            다름을 만드는 AI 활용 교육
          </h2>
          <p className="text-base md:text-lg max-w-3xl mx-auto font-medium leading-relaxed" style={{ color: 'rgba(255,255,255,0.9)' }}>
            AI 시대에 필요한 실용적인 지식과 기술을 누구나 쉽게 배울 수 있도록 교육합니다.<br />
            ChatGPT, 이미지 생성 AI 등 최신 AI 도구 활용법부터 비즈니스 적용까지,<br />
            체계적인 커리큘럼으로 AI 활용 능력을 키워드립니다.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {aiActivities.map((activity) => (
            <div
              key={activity.id}
              onClick={() => handleCardClick(activity.link)}
              className={`group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 ${activity.link ? 'cursor-pointer' : 'cursor-default'}`}
              style={{ height: '480px' }}
            >
              {/* 배경 이미지 */}
              <img
                src={activity.thumbnail}
                alt={activity.title}
                className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
              />

              {/* 그라데이션 오버레이 */}
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.45) 50%, rgba(0,0,0,0.15) 100%)' }}></div>

              {/* 콘텐츠 */}
              <div className="absolute inset-0 flex flex-col justify-end p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 rounded-full text-xs font-bold text-white" style={{ backgroundColor: activity.badgeColor }}>
                    {activity.badge}
                  </span>
                  {activity.date && (
                    <span className="text-xs font-medium text-white/80">{activity.date}</span>
                  )}
                </div>

                <h3 className="text-xl md:text-2xl font-extrabold text-white mb-3 leading-snug drop-shadow-md">
                  {activity.title}
                </h3>

                <p className="text-sm text-white/85 leading-relaxed mb-4 drop-shadow-sm">
                  {activity.description}
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-white/90">
                    <i className="ri-user-line"></i>
                    <span>대상: {activity.target}</span>
                  </div>
                  {activity.link && (
                    <div className="flex items-center gap-1 text-xs font-semibold text-white/80 group-hover:text-white transition-colors duration-300">
                      <span>자세히 보기</span>
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 영화와 인문학 섹션 앵커 */}
        <div id="film" style={{ marginTop: '-1px' }}></div>

        <div className="text-center mt-10 md:mt-12">
          <button className="px-8 py-3 rounded-full font-semibold hover:shadow-lg transition-all duration-300 whitespace-nowrap text-sm md:text-base cursor-pointer" style={{ backgroundColor: '#DFFF00', color: '#3b0764' }}>
            <i className="ri-book-2-line mr-2"></i>
            강의 신청하기
          </button>
        </div>
      </div>
    </section>
  );
}
